import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralDatePicker, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { TRANSACTION_PACKAGE_LIST } from 'src/constants/queryKeys';
import { networkCreateTransactionPackage, networkUpdateTransactionPackage } from 'src/network/transaction-package';
import useFormsModalStore from 'src/store/forms';
import { calculateDiscountedPrice } from 'src/utils';
import { getDefaultValues } from './defaultValues';

const TransactionPackageForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const transactionPackageDetails = modal?.data;
  const isEditMode = !!transactionPackageDetails;

  const queryClient = useQueryClient();

  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [startDate, setStartDate] = useState(
    transactionPackageDetails?.TransactionPackageDateStart ? new Date(transactionPackageDetails?.TransactionPackageDateStart) : new Date()
  );

  const [endDate, setEndDate] = useState(
    transactionPackageDetails?.TransactionPackageDateEnd ? new Date(transactionPackageDetails?.TransactionPackageDateEnd) : new Date()
  );

  const defaultValues = getDefaultValues(transactionPackageDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    watch,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      TransactionPackageApplicationID: +projectId,
      TransactionPackageAmountOrginal: +values?.TransactionPackageAmountOrginal,
      TransactionPackageDiscountPercent: +values?.TransactionPackageDiscountPercent,
      TransactionPackageOrderIndex: +values?.TransactionPackageOrderIndex,
      TransactionPackageAmountFinal: calculateDiscountedPrice(
        +values?.TransactionPackageAmountOrginal,
        +values?.TransactionPackageDiscountPercent
      ),
      TransactionPackageIsActive: values?.TransactionPackageIsActive ? 1 : 0,
      TransactionPackageCanUseOnce: values?.TransactionPackageCanUseOnce ? 1 : 0,
      TransactionPackageDateStart: new Date(startDate).toISOString(),
      TransactionPackageDateEnd: new Date(endDate).toISOString(),
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateTransactionPackage(transactionPackageDetails.TransactionPackageID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('نرخ نامه موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [TRANSACTION_PACKAGE_LIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateTransactionPackage(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('نرخ نامه موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            await queryClient.invalidateQueries({ queryKey: [TRANSACTION_PACKAGE_LIST] });
          }
          reset();
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const amountOrginal = watch('TransactionPackageAmountOrginal');
  const discountPercent = watch('TransactionPackageDiscountPercent');

  useEffect(() => {
    if (amountOrginal && discountPercent !== undefined) {
      const finalAmount = calculateDiscountedPrice(parseFloat(amountOrginal), parseFloat(discountPercent));
      setValue('TransactionPackageAmountFinal', finalAmount?.toLocaleString());
    }
  }, [amountOrginal, discountPercent, setValue]);

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField name="TransactionPackageLable" type="text" label="عنوان بسته نرخ نامه" />
                <GeneralUpload
                  label="تصویر نمایشی"
                  id="trasaction_package_logo"
                  name="TransactionPackageLogo"
                  afterChangeAction={(res) => {
                    setValue('TransactionPackageLogo', res);
                    trigger('TransactionPackageLogo');
                  }}
                />

                <RHFTextField name="TransactionPackageAmountOrginal" type="text" label="ارزش بسته به تومان" />
                <RHFTextField name="TransactionPackageDiscountPercent" type="text" label="درصد تخفیف" />

                <RHFTextField name="TransactionPackageAmountFinal" type="text" label="مبلغ نهایی قابل پرداخت توسط کاربر" readOnly={true} />
                <RHFTextField name="TransactionPackageOrderIndex" type="text" label="ترتیب نمایش" />

                <GeneralDatePicker
                  label="تاریخ شروع نرخ نامه"
                  name="TransactionPackageDateStart"
                  value={startDate}
                  onChangeValue={setStartDate}
                />
                <GeneralDatePicker
                  minDate={startDate}
                  label="تاریخ پایان نرخ نامه"
                  name="TransactionPackageDateEnd"
                  value={endDate}
                  onChangeValue={setEndDate}
                />

                <RHFSwitch name="TransactionPackageIsActive" labelPlacement="end" label="نرخ نامه به کاربران نشان داده شود (فروشگاه)" />
                <RHFSwitch
                  name="TransactionPackageCanUseOnce"
                  labelPlacement="end"
                  label="کاربر یک بار میتواند از این نرخ نامه استفاده کند"
                />
              </Box>

              <div className="flex items-center gap-4 mt-[14rem]">
                <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                  ذخیره
                </LoadingButton>
                <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                  {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                </LoadingButton>
                <Button onClick={closeModal} variant="contained" color="error">
                  انصراف
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};

export default TransactionPackageForm;
